import type { Href } from 'expo-router';

/**
 * Maps a push notification's `data.target` to a safe in-app route.
 *
 * The backend sends `data: { type, target }` (see `notifications/dispatcher.rb`
 * and `broadcast_job.rb`). `target` is the template's `deep_link_target`
 * (seeded "home" | "referrals" | "deal") or a broadcast's chosen target
 * ("home" | "deals" | "leaderboard" | "referrals"). Target "deal" carries
 * `data.deal_slug` (the step_confirmed push) and opens that deal screen; a
 * slug that is not a plain slug falls back to home. Anything we don't
 * recognise — a target added server-side before the app ships a matching
 * screen, or a malformed payload — falls back to home rather than crashing or
 * dumping the user on +not-found. Mirrors the allowlist stance of
 * `safeInternalPath`.
 */
const TARGET_ROUTES: Record<string, string> = {
  home: '/',
  deals: '/deals',
  leaderboard: '/leaderboard',
  referrals: '/referrals',
  earnings: '/earnings',
};

export type NotificationData = {
  type?: unknown;
  target?: unknown;
  delivery_id?: unknown;
  deal_slug?: unknown;
};

const DEAL_TARGET = 'deal';
// Same shape the router accepts for /deals/[slug]; rejects anything that
// could smuggle a path segment or query into the route.
const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{0,99}$/;

/**
 * The delivery row this notification came from, or null.
 *
 * Same defensive stance as `routeForNotificationData` above: anything that
 * isn't a positive integer — a push sent before open tracking shipped (which
 * carries no `delivery_id` at all), a malformed payload, a future server
 * change — resolves to null and the tap simply goes unlogged. Expo hands
 * `data` back as JSON, so a number stays a number, but a string is accepted
 * too rather than silently dropping every open on a serialization change.
 */
export function deliveryIdFromNotificationData(data: unknown): number | null {
  const raw = (data as NotificationData | null | undefined)?.delivery_id;
  const id = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
  return Number.isInteger(id) && id > 0 ? id : null;
}

/** Resolve the tap destination for a notification's `data` payload. */
export function routeForNotificationData(data: unknown): Href {
  const payload = data as NotificationData | null | undefined;
  const target = payload?.target;
  if (target === DEAL_TARGET) {
    const slug = payload?.deal_slug;
    if (typeof slug === 'string' && SLUG_PATTERN.test(slug)) return `/deals/${slug}` as Href;
    return '/' as Href;
  }
  const path = typeof target === 'string' ? TARGET_ROUTES[target] : undefined;
  // Cast mirrors safeInternalPath: typed routes only guard literals at compile
  // time, and every value here is a known internal path (or the '/' fallback).
  return (path ?? '/') as Href;
}
